import React, { useState, useCallback } from 'react';
import { X, Send, Lightbulb } from 'lucide-react';
import { featureRequestsApi } from '../../services/api';

const ANIMATION_DURATION = 150;

function FeatureRequestModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    title: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, ANIMATION_DURATION);
  }, [isClosing, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.title.trim() || !formData.description.trim()) {
      setError('Le titre et la description sont obligatoires');
      return;
    }

    setIsSubmitting(true);

    try {
      await featureRequestsApi.create({
        title: formData.title.trim(),
        description: formData.description.trim()
      });

      // Reset form
      setFormData({ title: '', description: '' });
      
      // Call success callback
      if (onSuccess) {
        onSuccess();
      }

      // Close modal with animation
      handleClose();
    } catch (error) {
      console.error('Error submitting feature request:', error);
      setError(error.message || 'Erreur lors de l\'envoi de la suggestion');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 overlay-frost flex items-center justify-center p-4 z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}>
      <div className={`glass-deep border border-white/10 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${isClosing ? 'animate-modal-out' : 'animate-modal-in'}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <div className="bg-yellow-500/20 p-2 rounded-lg">
              <Lightbulb className="h-6 w-6 text-yellow-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                Suggérer une nouvelle fonctionnalité
              </h2>
              <p className="text-sm text-gray-400">
                Aidez-nous à améliorer ShinySmile
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-200 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-md p-4">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-300 mb-2">
              Titre de la suggestion *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Ex: Ajout d'un système de notifications email"
              className="w-full px-3 py-2 bg-black/30 border border-white/10 text-white placeholder-gray-500 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              disabled={isSubmitting}
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-2">
              Description détaillée *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="6"
              placeholder="Décrivez votre idée en détail : quel problème cela résoudrait-il ? Comment cela fonctionnerait-il ? Qui en bénéficierait ?"
              className="w-full px-3 py-2 bg-black/30 border border-white/10 text-white placeholder-gray-500 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-vertical"
              disabled={isSubmitting}
            />
          </div>

          {/* Info box */}
          <div className="bg-yellow-600/10 border border-yellow-600/30 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <Lightbulb className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-400">
                  Conseils pour une bonne suggestion
                </h3>
                <div className="mt-2 text-sm text-yellow-300">
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Soyez précis sur le problème que vous rencontrez</li>
                    <li>Expliquez comment la fonctionnalité vous aiderait</li>
                    <li>Donnez des exemples d'usage si possible</li>
                    <li>Mentionnez si d'autres utilisateurs ont exprimé le même besoin</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-300 bg-white/15 border border-white/15 rounded-md hover:bg-white/20 transition-colors"
              disabled={isSubmitting}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.title.trim() || !formData.description.trim()}
              className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:bg-white/10 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              <Send className="h-4 w-4" />
              <span>{isSubmitting ? 'Envoi...' : 'Envoyer la suggestion'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default FeatureRequestModal;